/* global __GDC_PORTAL__ */

/* @fflow */
import React, { Component, useEffect } from 'react';
import PropTypes from 'prop-types';
// $FlowIgnore
import { connect, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';
import { fetchUser } from 'ducks/auth';
import { resetMessage } from 'ducks/message';
import Spinner from 'components/UIKit/Spinner/Spinner';
import { ExternalLink } from 'components/Links';
import CenteredLayout from 'components/layouts/CenteredLayout';
import { GDCLogo } from 'components/Theme';
import { Row, Column } from 'components/UIKit/Flex';
import Card from 'components/UIKit/Card';
import GDCAppsDropdown from 'components/GDCAppsDropdown';
import Banner from 'components/UIKit/Banner';
import { dismissNotification } from 'ducks/notifications';
import withRouting from 'utils/withRouting';

import { SUBMIT_ICON } from 'views/images';

class Login extends Component {
  static propTypes = {
    location: PropTypes.object.isRequired,
    dispatch: PropTypes.func,
    auth: PropTypes.object,
  };

  state = {
    attemptedLogin: false,
    loggingIn: false,
  };

  openAuthWindow = (): void => {
    const { router } = this.props;
    const { query: { next }, basename } = router.location;
    const url = `${__GDC_PORTAL__}/auth?next=${next || basename}`;
    const win = open(url, 'Auth', 'width=1000, height=800');
    const { dispatch } = this.props;

    this.setState({ loggingIn: true });

    const interval = setInterval(() => {
      try {
        if (win.closed || !win.document) {
          // window is closed
          clearInterval(interval);
          this.setState({ loggingIn: false });
        } else if (win.document.URL !== url && win.document.URL.indexOf('/submission') > -1) {
          /*
            * When the popup redirects back to the submission ui
            * after logging in, the cross domain error is no longer present
            * making `win.document.URL` truthy
            */
          clearInterval(interval);
          win.close();
          dispatch(resetMessage());
          setTimeout(() => {
            dispatch(fetchUser())
              .then(() => this.setState({ attemptedLogin: true, loggingIn: false }))
              .catch(() => console.log('dispatch fetch user errored'));
          }, 1000);
        }
      } catch (err) {
        console.log(err);
      }
    }, 500);
  };

  logout(): void {
    const { router } = this.props;
    const { query: { next }, basename } = router.location;
    const redirect = `/auth/logout?next=${next || basename}`;
    location.assign(redirect);
  }

  renderWarning(message: any): React.Element {
    return (
      <div
        style={{
          width: '55rem',
          backgroundColor: '#fff',
          border: '1px solid orangered',
          display: 'flex',
          alignItems: 'center',
          padding: '.5rem',
          margin: '.5rem auto .5rem auto',
        }}
        >
        <i
          className="material-icons"
          style={{
            marginLeft: '1rem',
            color: 'orangered',
          }}
          >
error_outline
        </i>
        <div style={{ marginLeft: '.5rem' }}>
          { message }
        </div>
      </div>
    );
  }

  renderButton(noDbGap) {
    const { loggingIn } = this.state;
    const { auth: { user: { username = '' } } } = this.props;
    const buttonText = loggingIn ? 'Logging In...' : (noDbGap ? `Logout ${username}` : 'Login');
    const icon = noDbGap ? 'exit_to_app' : 'lock';

    return (
      <a
        data-testid="button-login"
        onClick={() => (noDbGap ? this.logout() : this.openAuthWindow())}
        className="mdl-button"
        style={{
          padding: '.5rem',
          textTransform: 'none',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        >
        <i
          style={{
            marginRight: '.25rem',
            fontSize: '1.4rem',
          }}
          className="material-icons"
          >
          { icon }
        </i>
        { buttonText }
      </a>
    );
  }

  render(): React.Element {
    const { attemptedLogin, loggingIn } = this.state;
    const {
      auth, dispatch, notifications, router: { location: { query: { sessionExpired } } },
    } = this.props;
    const noDbGap = auth.user.username && !auth.isAuthorized;
    const sessionExpiredJSON = JSON.parse(sessionExpired || false);
    const appName = 'GDC Data Submission Portal';

    if (auth.firstLoad) return null;

    return (
      <Column style={{ flex: 1 }}>
        <Column>
          {notifications.map(n => (
            <Banner
              {...n}
              key={n.id}
              handleOnDismiss={() => dispatch(dismissNotification(n.id))}
              />
          ))}
        </Column>
        <Row style={{ height: '3.5rem', backgroundColor: '#fafafa' }}>
          <Row
            style={{
              order: 3,
              flex: 1,
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
            >
            <GDCAppsDropdown
              style={{
                border: '1px solid #efefef',
                borderRight: 'none',
              }}
              />
          </Row>
        </Row>
        <CenteredLayout>
          <Row style={{ height: '3.5rem', backgroundColor: '#fafafa' }} />
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              opacity: loggingIn ? 1 : 0,
              transition: 'opacity 0.3s ease',
              backgroundColor: 'rgba(255, 255, 255, 0.66)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxSizing: 'border-box',
              padding: '8rem',
              zIndex: 999,
            }}
            >
            <div
              style={{
                backgroundColor: 'white',
                border: '2px solid rgb(96, 125, 139)',
                borderRadius: '1rem',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              >
              <div style={{ padding: '2rem 2rem 0rem 2rem' }}>Logging In...</div>
              <div style={{ padding: '2rem' }}><Spinner /></div>
            </div>
          </div>
          <Row>
            <GDCLogo style={{ height: '7rem' }} />
          </Row>
          <Row>
            { attemptedLogin && noDbGap && this.renderWarning((<span>
Warning: Your ERA Commons account does not have dbGaP access to projects hosted by the GDC.
          Please request access to dbGap, click
              <a href="https://gdc.cancer.gov/access-data/obtaining-access-controlled-data">here</a>
              {' '}
for more information.
            </span>)) }
            { ((!loggingIn && attemptedLogin) || sessionExpiredJSON) && (auth.error && auth.error.message)
              && this.renderWarning(auth.error.message)
            }
          </Row>
          <Row>
            <Column style={{ flex: 2 }}>
              <Card title="welcome to the gdc data submission portal">
                <p>
                  The
                  {' '}
                  { appName }
                  {' '}
is a web-based system that allows researchers to
                  submit and release clinical, biospecimen, and experimental data for
                  studies registered in dbGaP into GDC. The
                  <ExternalLink customDataTestID="link-data-submission-portal-users-guide" href="https://docs.gdc.cancer.gov/Data_Submission_Portal/Users_Guide/Checklist/">
                    { appName }
                    {' '}
User’s Guide
                  </ExternalLink>
                  {' '}
provides detailed instructions for submitting
                  <ExternalLink customDataTestID="link-supported-data-types-and-file-formats" href="https://gdc.cancer.gov/about-data/data-types-and-file-formats">
                  GDC Supported data types and file formats
                  </ExternalLink>
.
                </p>
              </Card>
            </Column>
            <Column style={{ flex: 1 }}>
              <Card title="get started with data submissions">
                { this.renderButton(attemptedLogin && noDbGap) }
                { auth.isFetching && (
                  <Row
                    style={{
                      color: '#ccc', justifyContent: 'center', alignItems: 'center', paddingTop: '0.5rem',
                    }}
                    >
                    <Spinner style={{ width: '18px', height: '18px', marginRight: '0.5rem' }} />
Checking token....
                  </Row>
                )}
              </Card>
              <Row
                style={{
                  display: 'flex',
                  marginTop: '1rem',
                  justifyContent: 'space-around',
                  alignItems: 'center',
                }}
                >
                <div>
                  <i
                    style={{
                      marginRight: '.25rem',
                      fontSize: '1.4rem',
                      color: '#666',
                    }}
                    className="material-icons"
                    >
arrow_forward
                  </i>
                  <ExternalLink customDataTestID="link-view-gdc-data-submission-policies" href="https://gdc.cancer.gov/submit-data/data-submission-policies">
                    <strong>View GDC data submission policies</strong>
                  </ExternalLink>
                  <br />

                  <i
                    style={{
                      marginRight: '.25rem',
                      fontSize: '1.4rem',
                      color: '#666',
                    }}
                    className="material-icons"
                    >
arrow_forward
                  </i>
                  <ExternalLink customDataTestID="link-apply-for-data-submission" href="https://gdc.cancer.gov/submit-data/obtaining-access-submit-data">
                    <strong>
Apply for data submission
                      <br />
                      {' '}
authorization through dbGaP
                    </strong>
                  </ExternalLink>
                </div>
                <img style={{ height: '3.5rem' }} src={SUBMIT_ICON} alt="" />
              </Row>
            </Column>
          </Row>
          <Column>
            <Card>
              <p>You are accessing a U.S. Government web site which may contain information that must be protected under the U. S. Privacy Act or other sensitive information and is intended for Government authorized use only.</p>
              <p>Unauthorized attempts to upload information, change information, or use of this web site may result in disciplinary action, civil, and/or criminal penalties. Unauthorized users of this web site should have no expectation of privacy regarding any communications or data processed by this web site.</p>
              <p>Anyone accessing this web site expressly consents to monitoring of their actions and all communication or data transiting or stored on or related to this web site and is advised that if such monitoring reveals possible evidence of criminal activity, NIH may provide that evidence to law enforcement officials.</p>
            </Card>
          </Column>
        </CenteredLayout>
      </Column>
    );
  }
}

export { Login as UnconnectedLogin };

const LoginWithRedirect = (props) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (props.auth.isAuthorized) {
      const { state } = props.router.location || {};
      const { location } = state || {};
      const { pathname, search } = location || {};
      const path = pathname ? `${pathname}${search}` : '/';

      navigate(path);
    }
  }, [props.auth.isAuthorized]);

  return <Login {...props} />
}

export default connect(
  state => ({
    auth: state.auth,
    notifications: state.notifications,
  }),
)(withRouting(LoginWithRedirect));
